import React, { useEffect, useRef } from "react";
import * as d3 from "d3";

const ForceGraph = ({ settings: propSettings, jsonData, categoryColors, onClicked }) => {
  const svgRef = useRef();

  useEffect(() => {
    const svg = d3.select(svgRef.current);
    svg.selectAll("*").remove();

    const width = propSettings.width || svg.node().getBoundingClientRect().width;
    const height = propSettings.height;

    const defaultLocalSettings = {
      initialTransition: {
        duration: 1000,
        ease: d3.easeCubicOut
      },
      circleGrow: {
        duration: 1000,
        ease: d3.easeCubicOut
      },
      fadeOut: {
        maxDelay: 500,
        duration: 500
      },
      labelFade: {
        delays: [300, 500],
        duration: 700
      }
    };

    const localSettings = {
      initialTransition: {
        ...defaultLocalSettings.initialTransition,
        ...(propSettings.animation?.initialTransition || {})
      },
      circleGrow: {
        ...defaultLocalSettings.circleGrow,
        ...(propSettings.animation?.circleGrow || {})
      },
      fadeOut: {
        ...defaultLocalSettings.fadeOut,
        ...(propSettings.animation?.fadeOut || {})
      },
      labelFade: {
        ...defaultLocalSettings.labelFade,
        ...(propSettings.animation?.labelFade || {})
      }
    };

    const layoutSettings = {
      circleScaleRatio: propSettings.circleScaleRatio || 1,
      chargeStrength: propSettings.chargeStrength || -50,
      collisionPadding: propSettings.collisionPadding || 2,
      centerY: propSettings.centerY || height / 2,
      simulationTicks: propSettings.simulationTicks || 300,
      border: propSettings.border || 4,
      font: propSettings.font || "12px sans-serif"
    };

    let scalingOnDataLength = 1
    if(jsonData?.length>14) {
      // NOTE: BELOW IS A RESPONSIVE CODE BASED ON DATA LENGTH
      scalingOnDataLength = 0.5
      layoutSettings.circleScaleRatio = 0.72
      layoutSettings.font = '13px poppins'
      if(jsonData?.length>25) {
        scalingOnDataLength = 0.4
        layoutSettings.circleScaleRatio = 0.62
        layoutSettings.font = '12px poppins'
      }
    }
    // if(width < 1200) {
    //   // NOTE: BELOW IS A RESPONSIVE CODE BASED ON DATA LENGTH
    //   scalingOnDataLength = 0.4
    //   layoutSettings.circleScaleRatio = 0.62
    //   layoutSettings.font = '12px poppins'
    // }


    // Prepare nodes
    const nodes = jsonData.map(d => {
      const baseSize = (d.size / 2) * layoutSettings.circleScaleRatio;
      return {
        ...d,
        id: d.source.replaceAll(" ", ""),
        color: categoryColors[d.category.replaceAll(" ", "")] || "#ccc",
        size: baseSize,
        rest: d.rest,
        sourceArray: d.sourceArray,
        details: d.details.map(s => ({
          ...s,
          size: (s.size / 2) * layoutSettings.circleScaleRatio
        }))
      };
    });

    // Force simulation
    const simulation = d3
      .forceSimulation(nodes)
      .force("charge", d3.forceManyBody().strength(layoutSettings.chargeStrength * scalingOnDataLength))
      .force("center", d3.forceCenter(width / 2, height / 2))
      .force(
        "collision",
        d3.forceCollide().radius(d => d.size + layoutSettings.border + layoutSettings.collisionPadding * scalingOnDataLength)
        // .strength(0.7)  // you can also lower this < 1
        // .iterations(1)  // reduce iterations
      )
      .force("y", d3.forceY(layoutSettings.centerY).strength(0.02));

    // Pre‑tick to stabilize
    for (let i = 0; i < layoutSettings.simulationTicks; i++) {
      simulation.tick();
    }

    // Create node groups
    const nodeGroup = svg
      .selectAll("g")
      .data(nodes)
      .enter()
      .append("g")
      .attr("transform", `translate(${width / 2}, ${height / 2})`)
      .style("opacity", 0);

    // Entrance transition
    nodeGroup
      .transition()
      .duration(localSettings.initialTransition.duration)
      .ease(localSettings.initialTransition.ease)
      .attr("transform", d => `translate(${d.x},${d.y})`)
      .style("opacity", 1);

    // Append circles
    nodeGroup
      .append("circle")
      .attr("r", 0)
      .attr("stroke", d => d.color)
      .attr("stroke-width", layoutSettings.border)
      .attr("fill", "#FFFFFF")
      .attr("class", "nodeCircle")
      .on("click", (event, clickedNode) => {
        nodeGroup.selectAll("circle").style("pointer-events", "none");

        const t = nodeGroup
          .transition()
          .delay((d, i) => Math.random() * localSettings.fadeOut.maxDelay)
          .duration(localSettings.fadeOut.duration)
          .style("opacity", 0);

        if (t.end) {
          t.end().then(() => {
            onClicked(clickedNode);
          });
        } else {
          endAll(t, () => {
            onClicked(clickedNode);
          });
        }
      })
      .transition()
      .duration(localSettings.circleGrow.duration)
      .ease(localSettings.circleGrow.ease)
      .attr("r", d => d.size);

    // Append labels, centering single line
    nodeGroup.each(function (d) {
      const group = d3.select(this);
      const margin = 4;
      const sign = d.x < width / 2 ? 1 : -1;
      const xOffset = sign * (d.size + layoutSettings.border + margin);
      const anchor = sign > 0 ? "start" : "end";

      const lines = d.sourceArray || [];
      const numLines = lines.length;

      if (numLines === 1) {
        // single line — center vertically
        group
          .append("text")
          .attr("x", xOffset)
          .attr("y", 0)
          .style("font", layoutSettings.font)
          .attr("text-anchor", anchor)
          .style("opacity", 0)
          .text(lines[0])
          .transition()
          .delay(localSettings.labelFade.delays[0])
          .duration(localSettings.labelFade.duration)
          .style("opacity", 1);
      } else {
        // two (or more) lines — show first two, one above and one below
        group
          .append("text")
          .attr("x", xOffset)
          .attr("y", -7)
          .style("font", layoutSettings.font)
          .attr("text-anchor", anchor)
          .style("opacity", 0)
          .text(lines[0])
          .transition()
          .delay(localSettings.labelFade.delays[0])
          .duration(localSettings.labelFade.duration)
          .style("opacity", 1);

        group
          .append("text")
          .attr("x", xOffset)
          .attr("y", 8)
          .style("font", layoutSettings.font)
          .attr("text-anchor", anchor)
          .style("opacity", 0)
          .text(lines[1])
          .transition()
          .delay(localSettings.labelFade.delays[1])
          .duration(localSettings.labelFade.duration)
          .style("opacity", 1);
      }
    });

    // Update positions on tick (if simulation later moves)
    simulation.on("tick", () => {
      nodeGroup
        .attr("transform", d => {
          // clamp y
          const r = d.size + layoutSettings.border + layoutSettings.collisionPadding;
          const minY = r;
          const maxY = propSettings.height - r;
          let y = d.y;
          if (y < minY) y = minY;
          if (y > maxY) y = maxY;

          // similarly clamp x if needed
          const minX = r;
          const maxX = width - r;
          let x = d.x;
          if (x < minX) x = minX;
          if (x > maxX) x = maxX;

          return `translate(${x},${y})`;
        });
    });


    function endAll(transition, callback) {
      let n = 0;
      transition.each(() => {
        ++n;
      }).on("end", () => {
        if (!--n) callback();
      });
    }

    return () => {
      simulation.stop();
    };
  }, [propSettings, jsonData, categoryColors, onClicked]);

  return (
    <svg
      ref={svgRef}
      height={propSettings.height + "px"}
      width="100%"
    ></svg>
  );
};

export default ForceGraph;
